import { FileText, Paperclip, X } from 'lucide-react'
import * as React from 'react'

/**
 * Anexos do chamado, no molde do formulário do Hub: foto, print colado com
 * Ctrl+V ou PDF, até 3, 10 MB cada. Nada sobe antes de o chamado existir; o
 * gateway confere o tipo pelos bytes.
 */

export const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024
const IMAGES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif']

export function isPdf(file: File): boolean {
  return file.type === 'application/pdf' || (!file.type && /\.pdf$/i.test(file.name))
}

export function acceptAttachments(
  current: readonly File[],
  incoming: readonly File[],
  max: number,
): { files: File[]; problem: string | null } {
  const files = [...current]
  let problem: string | null = null
  for (const file of incoming) {
    if (files.length >= max) {
      problem = `No máximo ${max} arquivos.`
      break
    }
    if (!isPdf(file) && file.type && !IMAGES.includes(file.type)) {
      problem = 'Envie foto, print ou PDF.'
      continue
    }
    if (file.size > MAX_ATTACHMENT_BYTES) {
      problem = 'Cada arquivo pode ter até 10 MB.'
      continue
    }
    files.push(file)
  }
  return { files, problem }
}

export function AttachmentPicker({
  files,
  onChange,
  max = 3,
  id,
}: {
  files: readonly File[]
  onChange: (files: File[]) => void
  max?: number
  id: string
}) {
  const input = React.useRef<HTMLInputElement>(null)
  const [problem, setProblem] = React.useState<string | null>(null)
  const previews = React.useMemo(() => files.map((f) => (isPdf(f) ? null : URL.createObjectURL(f))), [files])
  React.useEffect(() => () => previews.forEach((u) => u && URL.revokeObjectURL(u)), [previews])

  const add = React.useCallback(
    (incoming: readonly File[]) => {
      const result = acceptAttachments(files, incoming, max)
      setProblem(result.problem)
      onChange(result.files)
    },
    [files, max, onChange],
  )

  // Print colado com o diálogo aberto vira anexo.
  React.useEffect(() => {
    const onPaste = (event: ClipboardEvent) => {
      const pasted = [...(event.clipboardData?.files ?? [])].filter((f) => f.type.startsWith('image/'))
      if (pasted.length) {
        event.preventDefault()
        add(pasted)
      }
    }
    window.addEventListener('paste', onPaste)
    return () => window.removeEventListener('paste', onPaste)
  }, [add])

  return (
    <div className="space-y-2">
      {files.length > 0 && (
        <ul className="grid grid-cols-3 gap-2">
          {files.map((file, index) => {
            const preview = previews[index]
            return (
              <li key={`${file.name}-${index}`} className="relative aspect-square overflow-hidden rounded-md border border-border bg-muted">
                {preview ? (
                  <img src={preview} alt={`Anexo ${index + 1}`} className="h-full w-full object-cover" />
                ) : (
                  <span className="flex h-full w-full flex-col items-center justify-center gap-1 p-2 text-center">
                    <FileText className="h-6 w-6 text-muted-foreground" aria-hidden />
                    <span className="line-clamp-2 text-[12px] leading-tight [overflow-wrap:anywhere]">{file.name || 'documento.pdf'}</span>
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => onChange(files.filter((_, i) => i !== index))}
                  aria-label={`Remover ${file.name || `anexo ${index + 1}`}`}
                  className="absolute right-1 top-1 inline-flex h-7 w-7 items-center justify-center rounded-full bg-background/90 text-foreground shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </li>
            )
          })}
        </ul>
      )}
      {files.length < max && (
        <button
          type="button"
          onClick={() => input.current?.click()}
          className="flex h-10 w-full items-center justify-center gap-2 rounded-md border border-dashed border-border text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Paperclip className="h-4 w-4" aria-hidden />
          Anexar foto, print ou PDF
        </button>
      )}
      <input
        ref={input}
        id={id}
        type="file"
        accept="image/*,application/pdf,.pdf"
        multiple
        className="sr-only"
        tabIndex={-1}
        onChange={(e) => {
          add([...(e.target.files ?? [])])
          e.target.value = ''
        }}
      />
      {problem && (
        <p role="alert" className="text-[13px] text-destructive">
          {problem}
        </p>
      )}
    </div>
  )
}
